import {CommonRecentChat, FileValidationError} from "./message.model";

export enum GroupIconEnum {
    DEFAULT = "DEFAULT",
    DESIGN_TEAM = "DESIGN_TEAM",
    ENGINEERING_TEAM = "ENGINEERING_TEAM",
    OPERATIONAL_TEAM = "OPERATIONAL_TEAM",
    PRODUCT_TEAM = "PRODUCT_TEAM",
    MARKETING_TEAM = "MARKETING_TEAM",
    SALES_TEAM = "SALES_TEAM",
    CUSTOMER_SUPPORT_TEAM = "CUSTOMER_SUPPORT_TEAM",
    FINANCE_TEAM = "FINANCE_TEAM",
    IT_SUPPORT_TEAM = "IT_SUPPORT_TEAM",
    DATA_AND_ANALYTICS_TEAM = "DATA_AND_ANALYTICS_TEAM",
    BUSINESS_DEVELOPMENT_TEAM = "BUSINESS_DEVELOPMENT_TEAM",
    RISK_MANAGEMENT_TEAM = "RISK_MANAGEMENT_TEAM",
    LEGAL_TEAM = "LEGAL_TEAM",
    QA_TEAM = "QUALITY_ASSURANCE_TEAM",
    HUMAN_RESOURCES_TEAM = "HUMAN_RESOURCES_TEAM",
}

// Type for additional metadata
export interface GroupIconMeta {
    id: number;
    enumValue: GroupIconEnum;
    iconName: string; // icon file name used in assets
}

// Actual mapping
export const GROUP_ICON_META: GroupIconMeta[] = [
    { id: 1, enumValue: GroupIconEnum.DEFAULT, iconName: "hash" },
    { id: 2, enumValue: GroupIconEnum.DESIGN_TEAM, iconName: "pencil-ruler" },
    { id: 3, enumValue: GroupIconEnum.ENGINEERING_TEAM, iconName: "braces" },
    { id: 4, enumValue: GroupIconEnum.OPERATIONAL_TEAM, iconName: "cog" },
    { id: 5, enumValue: GroupIconEnum.PRODUCT_TEAM, iconName: "box" },
    { id: 6, enumValue: GroupIconEnum.MARKETING_TEAM, iconName: "megaphone" },
    { id: 7, enumValue: GroupIconEnum.SALES_TEAM, iconName: "dollar-sign" },
    { id: 8, enumValue: GroupIconEnum.CUSTOMER_SUPPORT_TEAM, iconName: "headset" },
    { id: 9, enumValue: GroupIconEnum.FINANCE_TEAM, iconName: "calculator" },
    { id: 10, enumValue: GroupIconEnum.IT_SUPPORT_TEAM, iconName: "network" },
    { id: 11, enumValue: GroupIconEnum.DATA_AND_ANALYTICS_TEAM, iconName: "database" },
    { id: 12, enumValue: GroupIconEnum.BUSINESS_DEVELOPMENT_TEAM, iconName: "rocket" },
    { id: 13, enumValue: GroupIconEnum.RISK_MANAGEMENT_TEAM, iconName: "shield" },
    { id: 14, enumValue: GroupIconEnum.LEGAL_TEAM, iconName: "scale" },
    { id: 15, enumValue: GroupIconEnum.QA_TEAM, iconName: "bug" },
    { id: 16, enumValue: GroupIconEnum.HUMAN_RESOURCES_TEAM, iconName: "users" },
];

// Optional helper
export function getGroupIconMeta(value: GroupIconEnum): GroupIconMeta | undefined {
    return GROUP_ICON_META.find((icon) => icon.enumValue === value);
}

export enum GroupColorEnum {
    DARK_GRAY = "DARK_GRAY",
    ORANGE = "ORANGE",
    BLUE = "BLUE",
    GREEN = "GREEN",
    RED = "RED",
    PURPLE = "PURPLE",
}

export const GROUP_COLORS: Record<GroupColorEnum, string> = {
    [GroupColorEnum.DARK_GRAY]: "#1D1D1F",
    [GroupColorEnum.ORANGE]: "#F69145",
    [GroupColorEnum.BLUE]: "#275CB2",
    [GroupColorEnum.GREEN]: "#56BA6F",
    [GroupColorEnum.RED]: "#D4494C",
    [GroupColorEnum.PURPLE]: "#9A56DE",
};

// Type guard to check if value is a valid enum
export function isValidGroupColorEnum(value: unknown): value is GroupColorEnum {
    return typeof value === 'string' && value in GROUP_COLORS;
}

// Type guard to check if value is a hex color
export function isHexColor(value: unknown): value is string {
    return typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value);
}

export function getColorCode(color: GroupColorEnum): string {
    return GROUP_COLORS[color];
}

export const ProfilePictureBackGroundColorPercent = "15"; // 15% opacity
export const DefaultGroupIcon = "hash"; // default icon name
export const DefaultUserIcon = "circle-user-round"; // default user icon name

export const ATTACHMENT_CONSTANTS = {
    MAX_FILE_SIZE: 40 * 1024 * 1024, // 40MB
    MAX_FILES_COUNT: 5, // Maximum number of files allowed in a single upload
    MAX_FILE_NAME_LENGTH: 100, // Maximum length of file name
    ALLOWED_FILE_TYPE_EXTENSIONS: [
        ".avi",
        ".csv",
        ".dot",
        ".html",
        ".mp3",
        ".mp4",
        ".mpeg",
        ".rar",
        ".zip",
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".bmp",
        ".pdf",
        ".doc",
        ".docx",
        ".ppt",
        ".pptx",
        ".xls",
        ".xlsx",
        ".txt",
        ".dwg",
        ".dxf",
        ".dng",
        ".m4a",
        ".webm",
        ".pbix",
        ".pbit",
        ".pdids",
        ".rdl",
        ".json",
        ".twb",
        ".twbx",
        ".heic",
        ".xlsm"
    ],
    API_ENDPOINTS: {
        UPLOAD_ATTACHMENTS: "/attachments/upload-attachments",
        DOWNLOAD_ATTACHMENT: "/attachments/download-attachment",
        /** Legacy byte-stream fallback for rows not yet migrated to S3 (PT-16085; removed with backend P16). */
        STREAM_ATTACHMENT: "/attachments/stream-attachment",
        /** Presigned S3 thumbnails by attachment ids (PT-16085 F8). */
        DOWNLOAD_THUMBNAILS: "/attachments/download-thumbnails",
    },
} as const;

export interface FileValidationConfig {
    maxFilesAllowed: number;
    maxFileNameLength: number;
    maxFileSizeBytes: number;
    allowedFileTypes: string[];
    allowedMimeTypes: string[];
    checkDuplicateNames: boolean;
    caseSensitiveDuplicateCheck: boolean;
}

export const DEFAULT_CONVERSATION_FILE_VALIDATION_CONFIG: FileValidationConfig = {
    maxFilesAllowed: ATTACHMENT_CONSTANTS.MAX_FILES_COUNT,
    maxFileNameLength: ATTACHMENT_CONSTANTS.MAX_FILE_NAME_LENGTH,
    maxFileSizeBytes: ATTACHMENT_CONSTANTS.MAX_FILE_SIZE,
    allowedFileTypes: ATTACHMENT_CONSTANTS.ALLOWED_FILE_TYPE_EXTENSIONS as unknown as string[],
    allowedMimeTypes: [],
    checkDuplicateNames: true,
    caseSensitiveDuplicateCheck: false,
};

export interface FileUploadValidationResponseV2 {
    isValid: boolean;
    validFiles: File[];
    invalidFiles: FileValidationError[];
    summary: {
        totalRequested: number;
        validCount: number;
        invalidCount: number;
        skippedDueToLimit: number;
        wouldExceedLimit: boolean;
    };
    errors: {
        unsupportedTypeFiles: FileValidationError[];
        filesExceedingSize: FileValidationError[];
        filesWithLongNames: FileValidationError[];
        duplicateNameFiles: FileValidationError[];
        emptyFiles: FileValidationError[];
        filesWithoutExtension: FileValidationError[];
        filesWithInvalidChars: FileValidationError[];
        filesExceedingCountLimit: FileValidationError[];
    };
    combineErrorMessage: string;
}

export interface DownloadAttachmentParams {
    messageId: number;
    messageAttachmentId: number;
    fileName: string;
}

export interface DownloadResponse {
    data: Blob;
    fileName: string;
}

/**
 * Represents the possible user presence statuses.
 * This enum provides a type-safe way to handle status IDs,
 * ensuring consistency with the backend.
 */
export enum IndicatorStatus {
    OFFLINE = 0,
    AVAILABLE = 1,
    IN_MEETING = 2,
    BUSY = 3,
    AWAY = 4,
    ON_LUNCH_BREAK = 5,
}

/**
 * Defines the structure for a single user's complete status information.
 */
export class UserStatus {
    userId: number;
    statusId: IndicatorStatus;
    status: string;
    customStatusMessage: string;
    /**
     * Constructs a UserStatusInConversation instance.
     * @param userId - The unique identifier for the user.
     * @param statusId - The user's current status ID from the IndicatorStatus enum.
     * @param status - A human-readable string representation of the user's status.
     * @param customStatusMessage - Any custom message set by the user for their status.
     */
    constructor( userId: number, statusId: IndicatorStatus, status: string, customStatusMessage: string) {
        this.userId = userId;
        this.statusId = statusId;
        this.status = status;
        this.customStatusMessage = customStatusMessage;
    }
}

/**
 * A constant object that maps the IndicatorStatus enum to its
 * human-readable string representation.
 *
 * Using "as const" provides two benefits:
 * 1. The properties become `readonly`.
 * 2. The values are inferred as literal types (e.g., 'OFFLINE') not just `string`.
 */
export const INDICATOR_STATUS_STRINGS = {
    [IndicatorStatus.OFFLINE]: 'OFFLINE',
    [IndicatorStatus.AVAILABLE]: 'AVAILABLE',
    [IndicatorStatus.IN_MEETING]: 'IN_MEETING',
    [IndicatorStatus.BUSY]: 'BUSY',
    [IndicatorStatus.AWAY]: 'AWAY',
    [IndicatorStatus.ON_LUNCH_BREAK]: 'ON_LUNCH_BREAK',
} as const;

export const INDICATOR_STATUS_LOWERCASE_STRINGS = {
    [IndicatorStatus.OFFLINE]: 'Offline',
    [IndicatorStatus.AVAILABLE]: 'Available',
    [IndicatorStatus.IN_MEETING]: 'In Meeting',
    [IndicatorStatus.BUSY]: 'Busy',
    [IndicatorStatus.AWAY]: 'Away',
    [IndicatorStatus.ON_LUNCH_BREAK]: 'On Lunch Break',
}

/**
 * A derived type that represents the union of all possible status strings.
 * e.g., 'OFFLINE' | 'AVAILABLE' | 'IN_MEETING' | ...
 * This is created programmatically from the INDICATOR_STATUS_STRINGS object.
 */
export type IndicatorStatusString = typeof INDICATOR_STATUS_STRINGS[keyof typeof INDICATOR_STATUS_STRINGS];

/**
 * Represents the structure of the incoming WebSocket message
 * when the server sends status updates for multiple users.
 */
export interface IndicatorMessage {
    messageType: 'INDICATOR'; // Literal type ensures this property can only be 'INDICATOR'
    data: Record<string, UserStatus> | UserStatus; // A dictionary mapping userId to UserStatus object
}

/**
 * Defines the payload structure for sending the current user's
 * status update to the WebSocket server.
 */
export interface UpdateStatusPayload {
    action: "INDICATOR";
    userId: number;
    status: IndicatorStatusString; // Now strongly typed!
    statusId: IndicatorStatus;
    customStatusMessage: string;
}

export function createStatusUpdate(newStatus: IndicatorStatus, customMessage: string, userId: number): UpdateStatusPayload {
    return {
        action: 'INDICATOR',
        statusId: newStatus,
        userId: userId,
        // Safely get the correct string from our constant using the enum member
        status: INDICATOR_STATUS_STRINGS[newStatus],
        customStatusMessage: customMessage,
    };
}
export class ActivateDeactivateGroupPayload {
    groupId: number;
    name: string | null
    orgId: number | null;
    isActive: boolean;

    constructor(groupId: number, name: string | null, orgId: number | null, isActive: boolean) {
        this.groupId = groupId;
        this.name = name;
        this.orgId = orgId;
        this.isActive = isActive;
    }
}
export interface MessageInfoRequest {
    messageId: number;
    senderId: number;
    receiverId?: number;
    groupId?: number;
}

export interface MessageInfoData {
    readUsers: { [userId: string]: string };
    deliveredUsers: { [userId: string]: string };
    noInfoUsers: number[];
}

export interface MessageInfoResponse {
    status: number;
    message: string;
    timestamp: string;
    data: MessageInfoData;
}

export interface UserInfo {
    userId: number;
    username: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
    handle?: string;
    timestamp?: string;
}

export interface TransformedMessageInfo {
    readUsers: UserInfo[];
    deliveredUsers: UserInfo[];
    noInfoUsers: UserInfo[];
}

export interface InfoDialogInput {
    infoData: MessageInfoData;
    selectedChat: CommonRecentChat;
}

export const DISPATCHER_EVENT = {
    TYPE: 'EVENT_DISPATCHER',
    TARGET: {
        PINNED_MESSAGE: 'PINNED_MESSAGE',
        STAR_MESSAGE: 'STAR_MESSAGE',
        GROUP_INFO: 'GROUP_INFO',
    },
    SUB_TARGET: {
        PINNED_MESSAGE_CREATED: 'PINNED_MESSAGE_CREATED',
        PINNED_MESSAGE_DELETED: 'PINNED_MESSAGE_DELETED',
        MESSAGE_STARRED: 'MESSAGE_STARRED',
        MESSAGE_UN_STARRED: 'MESSAGE_UN_STARRED',
        GROUP_CREATION: 'GROUP_CREATION',
        GROUP_DELETION: 'GROUP_DELETION',
        GROUP_CUSTOM_ACTIVE: 'GROUP_CUSTOM_ACTIVE',
        GROUP_CUSTOM_INACTIVE: 'GROUP_CUSTOM_INACTIVE',
        GROUP_MEMBER_ADDED: 'GROUP_MEMBER_ADDED',
        GROUP_MEMBER_REMOVED: 'GROUP_MEMBER_REMOVED',
        GROUP_ADMIN_ADDED: 'GROUP_ADMIN_ADDED',
        GROUP_ADMIN_REMOVED: 'GROUP_ADMIN_REMOVED',
        GROUP_DETAIL_UPDATED: 'GROUP_DETAIL_UPDATED',
        GROUP_CUSTOM_DETAIL_UPDATED: 'GROUP_CUSTOM_DETAIL_UPDATED',
    },
} as const;

export const CONVERSATION_MESSAGES = {
    REMOVED_FROM_GROUP: 'You are no longer a member of this group.',
    GROUP_DELETED: 'This group has been deleted.',
    ADDED_TO_GROUP: 'You have been added to the group',
} as const;